// Polyfill
import "core-js";
import "regenerator-runtime/runtime";
import "whatwg-fetch";

// Deps
import * as React from "react";
import { render } from "react-dom";
import * as firebase from "firebase/app";
import "firebase/auth";
import { useEffect, useState } from "react";
import {
  HashRouter as Router,
  Route,
  Switch,
  Redirect
} from "react-router-dom";
import { observer } from "mobx-react";
import { InquiryContact1 } from "./components/pages/InquiryContact1";
import { InquiryContact2 } from "./components/pages/InquiryContact2";
import { InquiryOrder } from "./components/pages/InquiryOrder";
import { InquiryDone } from "./components/pages/InquiryDone";
import { Signup } from "./components/pages/Signup";
import { Signin } from "./components/pages/Signin";
import { MagicLink } from "./components/pages/MagicLink";
import { ApplicationOrder } from "./components/pages/ApplicationOrder";
import { ApplicationContact } from "./components/pages/ApplicationContact";
import { ApplicationEstimate } from "./components/pages/ApplicationEstimate";
import { ApplicationDone } from "./components/pages/ApplicationDone";
import { ContactViewStore } from "./stores/ContactViewStore";
import { ContactViewStoreContext } from "./context/store";
import { css } from "@emotion/core";
import { Layout, Spin } from "antd";

// Settings
import "moment/locale/ja";

// @ts-ignore
import logo from "../img/contact-logo.png";
// @ts-ignore
import logo2x from "../img/contact-logo@2x.png";

firebase.initializeApp({
  apiKey: "AIzaSyAZVJzzws6DE2pT2aeBcE-ZftVM0msfqyk",
  authDomain: "postal-lp.firebaseapp.com",
  databaseURL: "https://postal-lp.firebaseio.com",
  projectId: "postal-lp",
  storageBucket: "postal-lp.appspot.com"
});

const containerCss = css`
  background: #fff;
  width: 100%;
  max-width: 1024px;
  padding: 0 20px;
  margin: 0 auto;

  @media (min-width: 576px) {
    padding: 0 50px;
  }
`;

const headerCss = css`
  ${containerCss}
  position: static;
  border-bottom: none;
  height: 100px;
`;

const contentCss = css`
  ${containerCss}
  padding-bottom: 20px;
`;

type SessionStatus = "unknown" | "loggedIn" | "loggedOut";

const App = observer(() => {
  // コンタクトページ全体で使うビューストア
  const [store, setStore] = useState(() => new ContactViewStore());

  // ユーザーのセッション情報(リダイレクト用)
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>("unknown");

  // Firebaseのセッション情報を監視する。
  useEffect(
    () =>
      firebase.auth().onAuthStateChanged(async user => {
        // セッションの状況をstateに反映。
        const newSessionStatus = user === null ? "loggedOut" : "loggedIn";
        setSessionStatus(newSessionStatus);

        if (newSessionStatus === "loggedIn" && !store.initialized) {
          // ログイン済み、かつストアの初期化が終わってないときのみデータを取得
          await store.reload();
          store.initialized = true;
        }

        if (sessionStatus === "loggedIn" && newSessionStatus === "loggedOut") {
          // ログアウトしたので、前のセッション情報を破棄する
          store.dispose();
          setStore(new ContactViewStore());
        }
      }),
    [sessionStatus, store]
  );

  return (
    <Layout
      css={css`
        background: #fff;
      `}
    >
      <Layout.Header css={headerCss}>
        <img
          css={css`
            margin-top: 15px;
            max-width: 100%;
          `}
          src={logo}
          srcSet={`${logo} 1x, ${logo2x} 2x`}
          alt="POSTAL WEDDING GIFT"
        />
        {store.internal && (
          <span
            css={css`
              font-weight: bold;
              margin-left: 20px;
            `}
          >
            運営用
          </span>
        )}
      </Layout.Header>
      <Layout.Content css={contentCss}>
        <ContactViewStoreContext.Provider value={store}>
          <Router>
            <Switch>
              {/* デフォルト */}
              <Route exact path="/">
                <Redirect to="/inquiry/contact1" />
              </Route>

              {/* 資料請求・お見積り */}
              <Route path="/inquiry/contact1" component={InquiryContact1} />
              <Route path="/inquiry/contact2">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/inquiry/contact1" />
                ) : store.initialized ? (
                  store.hasBasicContactInfo ? (
                    <Route component={InquiryContact2} />
                  ) : (
                    <Redirect to="/inquiry/contact1" />
                  )
                ) : (
                  <Spin />
                )}
              </Route>
              <Route path="/inquiry/order">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/inquiry/contact1" />
                ) : store.initialized ? (
                  store.hasAdvancedContactInfo && store.hasValidEmail ? (
                    <Route component={InquiryOrder} />
                  ) : (
                    <Redirect to="/inquiry/contact2" />
                  )
                ) : (
                  <Spin />
                )}
              </Route>
              <Route path="/inquiry/done">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/signin" />
                ) : (
                  <Route component={InquiryDone} />
                )}
              </Route>

              {/* お申込み */}
              <Route path="/signup">
                {sessionStatus === "loggedIn" ? (
                  <Redirect to="/application/order" />
                ) : (
                  <Route component={Signup} />
                )}
              </Route>
              <Route path="/signin">
                {sessionStatus === "loggedIn" ? (
                  <Redirect to="/application/order" />
                ) : (
                  <Route component={Signin} />
                )}
              </Route>
              <Route path="/m/:username/:password" component={MagicLink} />
              <Route path="/application/order">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/signin" />
                ) : store.initialized ? (
                  store.hasAdvancedContactInfo && store.hasValidEmail ? (
                    <Route component={ApplicationOrder} />
                  ) : (
                    <Redirect to="/application/contact" />
                  )
                ) : (
                  <Spin />
                )}
              </Route>
              <Route path="/application/contact">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/signin" />
                ) : store.initialized ? (
                  <Route component={ApplicationContact} />
                ) : (
                  <Spin />
                )}
              </Route>
              <Route path="/application/estimate">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/signin" />
                ) : store.initialized ? (
                  store.hasAllInfo ? (
                    <Route component={ApplicationEstimate} />
                  ) : (
                    <Redirect to="/application/order" />
                  )
                ) : (
                  <Spin />
                )}
              </Route>
              <Route path="/application/done">
                {sessionStatus === "loggedOut" ? (
                  <Redirect to="/signin" />
                ) : (
                  <Route component={ApplicationDone} />
                )}
              </Route>
            </Switch>
          </Router>
        </ContactViewStoreContext.Provider>
      </Layout.Content>
    </Layout>
  );
});

render(<App />, document.getElementById("app"));

window.onhashchange = () => {
  window.scrollTo(0, 0);
  // Send pageview.
  // @ts-ignore
  gtag("config", "UA-146954512-1", {
    page_location: location.pathname + location.search + location.hash
  });
};
